import React from 'react';
import { MessageCircle, Check } from 'lucide-react';

// Hyper-visible script line the rep reads verbatim to the customer.
export const ScriptCallout = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="bg-blue-50/80 border-l-4 border-blue-500 p-4 sm:p-5 rounded-r-xl mb-6 flex items-start gap-3 sm:gap-4 shadow-sm"
  >
    <MessageCircle className="text-blue-600 w-5 h-5 sm:w-6 sm:h-6 mt-0.5 shrink-0" strokeWidth={1.5} />
    <p className="text-blue-900 font-semibold text-base sm:text-lg leading-relaxed tracking-tight">
      {children}
    </p>
  </div>
);

export const SectionHeading = ({ children, hint }) => (
  <div className="mb-4">
    <h3 className="text-lg font-semibold tracking-tight text-slate-900">{children}</h3>
    {hint && <p className="text-sm text-slate-500 mt-0.5">{hint}</p>}
  </div>
);

export const ChoiceGrid = ({ children, cols = 2 }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-4`}>{children}</div>
);

export const ChoiceCard = ({ title, subtitle, icon: Icon, selected, onClick, testId, compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`relative text-left rounded-xl border-2 transition-all duration-200 hover:border-blue-300 hover:shadow-md focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-500/20 ${
      compact ? 'p-3' : 'p-5'
    } ${selected ? 'border-blue-600 bg-blue-50/40 shadow-sm' : 'border-slate-200 bg-white'}`}
  >
    {selected && (
      <span className={`absolute ${compact ? 'top-2 right-2 w-4 h-4' : 'top-3 right-3 w-5 h-5'} rounded-full bg-blue-600 text-white flex items-center justify-center`}>
        <Check className={compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} strokeWidth={3} />
      </span>
    )}
    <div className={`flex items-center ${compact ? 'gap-2.5' : 'gap-3'}`}>
      {Icon && (
        <span className={`rounded-lg flex items-center justify-center shrink-0 ${compact ? 'w-8 h-8' : 'w-10 h-10'} ${selected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
          <Icon className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
        </span>
      )}
      <div className="min-w-0">
        <div className={`font-bold text-slate-900 ${compact ? 'text-sm leading-tight' : 'text-base'}`}>{title}</div>
        {subtitle && <div className={`text-slate-500 font-normal ${compact ? 'text-xs leading-snug mt-0.5' : 'text-sm'}`}>{subtitle}</div>}
      </div>
    </div>
  </button>
);

const CIBIL_BAND = (score) => {
  const s = Number(score) || 0;
  if (!s) return { label: 'Pending', cls: 'text-slate-500 bg-slate-100 border-slate-200' };
  if (s >= 750) return { label: `${s} · Excellent`, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
  if (s >= 700) return { label: `${s} · Good`, cls: 'text-blue-700 bg-blue-50 border-blue-200' };
  if (s >= 650) return { label: `${s} · Fair`, cls: 'text-amber-700 bg-amber-50 border-amber-200' };
  return { label: `${s} · Poor`, cls: 'text-rose-700 bg-rose-50 border-rose-200' };
};

export const CibilBadge = ({ score, testId }) => {
  const b = CIBIL_BAND(score);
  return (
    <span data-testid={testId} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold border ${b.cls}`}>
      {b.label}
    </span>
  );
};

export const StatTile = ({ label, value, flag, flagType = 'positive', testId }) => (
  <div data-testid={testId} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col justify-between">
    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 line-clamp-1">{label}</div>
    <div className="text-xl font-bold text-slate-900">{value}</div>
    {flag && (
      <span className={`px-2 py-0.5 rounded text-[11px] font-bold mt-2 self-start ${flagType === 'positive' ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50'}`}>
        {flag}
      </span>
    )}
  </div>
);

export const CompletenessRing = ({ value = 0, size = 56 }) => {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="cockpit-completeness-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#2563eb" strokeWidth="6"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" className="transition-all duration-500" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-slate-900">{value}%</span>
    </div>
  );
};

export const inr = (n) => {
  if (n === null || n === undefined || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return `₹${num.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};
